// hospital.service.ts
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, catchError, retry, switchMap, tap, throwError, timer } from 'rxjs';
import { GroupService } from './group.service';
import { PatientService } from './patient.service';
import { PracticeSettingsStore } from './practice-settings-store.service';

/** Attempts after the first. Three tries total, ~1s + ~2s of waiting in the worst case. */
const HOSPITAL_FETCH_RETRIES = 2;
const HOSPITAL_FETCH_BASE_DELAY_MS = 1000;

/**
 * Retry a transient failure; give up immediately on a definite answer.
 *
 * A 401/403/404 is the server telling us something true — repeating the question cannot change
 * it, and retrying a 401 three times just delays the redirect to login. Status 0 is the browser's
 * "no response at all" (offline, DNS, CORS, a dropped connection), which is exactly the case worth
 * retrying, as is any 5xx.
 */
function isRetriableFetchError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;
  return error.status === 0 || error.status >= 500;
}

@Injectable({
  providedIn: 'root'
})
export class HospitalService {
  private baseUrl = 'api';
  private hospitalSubject = new BehaviorSubject<any>(null);
  hospitals$ = this.hospitalSubject.asObservable();
  groupId: any;

  constructor(private http: HttpClient, private groupService: GroupService, private patientService: PatientService, private settingsStore?: PracticeSettingsStore) {
    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          // catchError sits INSIDE the switchMap, on the inner observable, and that placement is
          // the whole point. An error allowed to reach the outer stream terminates this
          // subscription for the lifetime of the app — and this service is `providedIn: 'root'`,
          // so there is no second instance to recover it. `hospitalSubject` would keep its seed
          // value of `null` forever, and nothing else in the codebase calls
          // `getHospitalsByGroup` (only `updateHospitals`, from the settings screen), so a single
          // failed request would disable every hospital-dependent surface until a save or a
          // reload. Handled here, a failure costs one group's fetch and the next
          // `selectedGroup$` emission still works.
          return this.getHospitalsByGroup(selectedGroup.id, true).pipe(
            catchError(error => {
              console.error('HospitalService: hospitals could not be loaded for this group.', error);
              // Deliberately NOT `next([])`. An empty array is a claim — "this practice has no
              // hospitals" — and consumers act on it: `patient-list.component.ts` hides the
              // Inpatients tab on exactly that. We do not know how many hospitals there are, so
              // the subject keeps `null`, which means "not answered".
              return EMPTY;
            })
          );
        }
        return EMPTY;
      })
    ).subscribe();
  }

  /** `fromKeptCopy`: on a practice switch, the device's kept copy while it is current (PracticeSettingsStore). */
  getHospitalsByGroup(groupId: string, fromKeptCopy = false): Observable<any> {
    const token = localStorage.getItem('token');
    const request = this.http.get(`${this.baseUrl}/hospitals/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const source = fromKeptCopy && this.settingsStore ? this.settingsStore.read(groupId, 'hospitals', () => request) : request;
    return source.pipe(
      // `retry({ count, delay })` rather than the `retryWhen` helper in `app-data.service.ts:121`:
      // `retryWhen` is deprecated in RxJS 7 and removed in 8, and the config form expresses the
      // same exponential backoff without hand-rolling the counter. Retrying here rather than at
      // the call site means every caller benefits, including `updateHospitals`.
      retry({
        count: HOSPITAL_FETCH_RETRIES,
        delay: (error, retryCount) => {
          if (!isRetriableFetchError(error)) return throwError(() => error);
          const wait = HOSPITAL_FETCH_BASE_DELAY_MS * Math.pow(2, retryCount - 1);
          console.warn(`HospitalService: hospitals fetch failed, retry ${retryCount}/${HOSPITAL_FETCH_RETRIES} in ${wait}ms`, error);
          return timer(wait);
        }
      }),
      tap((hospitals: any) => {
        if (hospitals && hospitals.length > 0) {
          for (let h of hospitals) {
            h.wards = h.Wards;
            delete h.Wards;
          }
        }
        this.hospitalSubject.next(hospitals);
      })
    );
  }

  updateHospitals(hospitals: any[], deletedHospitalIds: string[], deletedWardIds: string[]): Observable<any> {
    const token = localStorage.getItem('token');
    hospitals.map(h => h.groupId = this.groupId);
    return this.http.put(`${this.baseUrl}/hospitals`, { hospitals, deletedHospitalIds, deletedWardIds }, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(() => this.getHospitalsByGroup(this.groupId)),
      switchMap(() => this.patientService.loadPatients(true))
    );
  }

  updateWardCoding(hospitals: any[]): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put(`${this.baseUrl}/hospitals/ward/coding`, { hospitals }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }
}
